import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash, IconUsers } from "@tabler/icons-react";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";

export default function RolePermissions({ roleId, permissions = [], onChange }) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedPermissionId, setSelectedPermissionId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    fetchCatalog();
  }, []);

  function fetchCatalog() {
    setLoadingCatalog(true);
    axios
      .post(`/retrieve-permissions/`, {
        scope: "all",
        page: 0,
        page_size: 500,
      })
      .then(({ data }) => {
        setCatalog(data.response[0]?.data || []);
        setLoadingCatalog(false);
      })
      .catch((err) => {
        setLoadingCatalog(false);
        toast.error(err);
      });
  }

  const assignedIds = new Set((permissions || []).map((p) => String(p.id)));
  const availableOptions = catalog
    .filter((p) => !assignedIds.has(String(p.id)))
    .map((p) => ({
      value: String(p.id),
      label: p.key ? `${p.name} (${p.key})` : p.name,
    }));

  function addPermission() {
    if (!selectedPermissionId) return;
    setBusyId(selectedPermissionId);
    axios
      .post(`/roles/${roleId}/permissions/`, {
        permission_id: selectedPermissionId,
      })
      .then(() => {
        setSelectedPermissionId(null);
        setBusyId(null);
        toast.success(t("updated"));
        onChange();
      })
      .catch((err) => {
        setBusyId(null);
        toast.error(err);
      });
  }

  function removePermission(permissionId) {
    setBusyId(permissionId);
    axios
      .post(`/remove-role-permission/`, {
        role_id: roleId,
        permission_id: permissionId,
      })
      .then(() => {
        setBusyId(null);
        toast.success(t("updated"));
        onChange();
      })
      .catch((err) => {
        setBusyId(null);
        toast.error(err);
      });
  }

  function clearDefault(permissionId) {
    setBusyId(permissionId);
    axios
      .post(`/set-default-role-permission/`, {
        role_id: roleId,
        permission_id: permissionId,
      })
      .then(() => {
        setBusyId(null);
        toast.success(t("updated"));
        onChange();
      })
      .catch((err) => {
        setBusyId(null);
        toast.error(err);
      });
  }

  function bulkAdd(permissionId) {
    setBusyId(permissionId);
    axios
      .post(`/bulk-permission-add/`, {
        role_id: roleId,
        permission_id: permissionId,
      })
      .then(() => {
        setBusyId(null);
        toast.success(t("updated"));
      })
      .catch((err) => {
        setBusyId(null);
        toast.error(err);
      });
  }

  return (
    <Stack gap="md">
      <Can permission={PERMISSIONS.ROLE_PERMISSIONS_MANAGE}>
        <Card>
          <Title order={5} mb="sm">
            {t("add")} {t("permission_singular")}
          </Title>
          {loadingCatalog ? (
            <Loader size="sm" />
          ) : (
            <Flex gap="sm" align="flex-end">
              <Select
                style={{ flex: 1 }}
                searchable
                clearable
                placeholder={t("permission_singular")}
                data={availableOptions}
                value={selectedPermissionId}
                onChange={setSelectedPermissionId}
              />
              <Button
                leftSection={<IconPlus size={14} />}
                disabled={!selectedPermissionId}
                loading={
                  !!selectedPermissionId && busyId === selectedPermissionId
                }
                onClick={addPermission}
              >
                {t("add")}
              </Button>
            </Flex>
          )}
        </Card>
      </Can>

      <Stack gap="xs">
        <Text fw={700}>
          {t("permission_plural")} ({(permissions || []).length})
        </Text>
        {!(permissions || []).length && <EmptyMessage />}
        {(permissions || []).map((permission) => (
          <Card key={permission.id}>
            <Flex gap="sm" align="center">
              <div style={{ flexGrow: 1 }}>
                <Group gap="xs">
                  <Text
                    fw={700}
                    component={Link}
                    to={`/permissions/${permission.id}`}
                    c="dark"
                    style={{ textDecoration: "none" }}
                  >
                    {permission.name}
                  </Text>
                  {permission.default && (
                    <Badge size="sm" variant="light" color="blue">
                      {t("default")}
                    </Badge>
                  )}
                </Group>
                {permission.description && (
                  <Text size="sm" c="dimmed">
                    {permission.description}
                  </Text>
                )}
                {permission.code != null && (
                  <Text size="xs" c="dimmed">
                    code {permission.code}
                  </Text>
                )}
              </div>
              <Can permission={PERMISSIONS.ROLE_PERMISSIONS_MANAGE}>
                <Flex gap="xs">
                  {permission.default && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="gray"
                      loading={busyId === permission.id}
                      onClick={() => clearDefault(permission.id)}
                    >
                      {t("clear_default")}
                    </Button>
                  )}
                  <ActionIcon
                    size="sm"
                    radius="xl"
                    variant="light"
                    color="blue"
                    title={t("bulk_grant_to_admins")}
                    loading={busyId === permission.id}
                    onClick={() => bulkAdd(permission.id)}
                  >
                    <IconUsers size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    radius="xl"
                    variant="light"
                    color="red"
                    title={t("remove")}
                    loading={busyId === permission.id}
                    onClick={() => removePermission(permission.id)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Flex>
              </Can>
            </Flex>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}
